import { useEffect, useState } from "react";
import { Button } from "./ui";
import { endSession, idleMinutes, keepAlive, lastActivity, markActivity } from "@/lib/auth";
import { useLanguage } from "@/i18n";

/** How long before the sign-out the "Are you still there?" dialog appears. */
const WARN_MS = 120_000;
const CHECK_MS = 5_000;
/** Activity is written at most this often -- mousemove fires constantly. */
const MARK_EVERY_MS = 10_000;
/** While someone is working, the server is told so at most this often. */
const KEEP_ALIVE_EVERY_MS = 5 * 60_000;

function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * Signs the portal out after the server's idle limit (IDLE_TIMEOUT_MINUTES_PORTAL)
 * with no mouse, keyboard or touch input.
 *
 * Two minutes before, an "Are you still there?" dialog counts down: Cancel
 * keeps the session, Sign out ends it now. At the limit the session is ended
 * (server and browser) and "Session timed out" stays until OK, which goes to
 * the login page.
 *
 * The limit is read on every check, not once: a tab left open across a change
 * of limit (or a sign-in in another tab) must use the current one, not the one
 * it started with.
 *
 * Only input counts as activity; the portal's own background requests do not.
 * While there is input, keepAlive() tells the server every few minutes, since
 * the server's idle clock otherwise sees only API requests and would end a
 * session someone is actively reading the map in.
 */
export default function IdleWatcher() {
  const { t } = useLanguage();
  const [msLeft, setMsLeft] = useState<number | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (timedOut) return;

    let lastMark = 0;
    let lastPing = Date.now();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastMark < MARK_EVERY_MS) return;
      lastMark = now;
      markActivity();
      if (now - lastPing >= KEEP_ALIVE_EVERY_MS) {
        lastPing = now;
        keepAlive();
      }
    };
    const events = [
      "mousemove", "mousedown", "pointerdown", "keydown", "touchstart", "wheel", "scroll",
    ] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }));

    let timer = 0;
    const check = () => {
      const limitMs = idleMinutes() * 60_000;
      if (limitMs <= 0) {
        setMsLeft(null);
        return;
      }
      const left = limitMs - (Date.now() - lastActivity());
      if (left <= 0) {
        window.clearInterval(timer);
        endSession();
        setMsLeft(null);
        setTimedOut(true);
        return;
      }
      setMsLeft(left <= WARN_MS ? left : null);
    };
    check();
    timer = window.setInterval(check, CHECK_MS);
    return () => {
      window.clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
    };
  }, [timedOut]);

  // A per-second countdown only while the warning is up.
  useEffect(() => {
    if (msLeft === null) return;
    const tick = window.setInterval(() => {
      setMsLeft(() => {
        const limitMs = idleMinutes() * 60_000;
        const left = limitMs - (Date.now() - lastActivity());
        return left <= WARN_MS && left > 0 ? left : left > 0 ? null : 0;
      });
    }, 1000);
    return () => window.clearInterval(tick);
  }, [msLeft === null]); // eslint-disable-line react-hooks/exhaustive-deps

  if (timedOut) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="idle-timeout-title"
          className="w-[min(92vw,380px)] rounded-md bg-surface-raised p-5 shadow-xl"
        >
          <h2 id="idle-timeout-title" className="text-sm font-semibold text-ink">
            {t.idle.timedOutTitle}
          </h2>
          <p className="mt-2 text-xs text-ink-secondary">{t.idle.signedOut(idleMinutes())}</p>
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              variant="primary"
              autoFocus
              onClick={() => {
                window.location.href = "/login";
              }}
            >
              {t.idle.ok}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (msLeft === null) return null;
  const idleFor = Math.max(1, Math.floor((idleMinutes() * 60_000 - msLeft) / 60_000));
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-warning-title"
        aria-live="assertive"
        className="w-[min(92vw,380px)] rounded-md bg-surface-raised p-5 shadow-xl"
      >
        <h2 id="idle-warning-title" className="text-sm font-semibold text-ink">
          {t.idle.stillThereTitle}
        </h2>
        <p className="mt-2 text-xs text-ink-secondary">{t.idle.stillThereBody(idleFor, mmss(msLeft))}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              endSession();
              window.location.href = "/login";
            }}
          >
            {t.idle.signOutNow}
          </Button>
          <Button
            size="sm"
            variant="primary"
            autoFocus
            onClick={() => {
              markActivity();
              keepAlive();
              setMsLeft(null);
            }}
          >
            {t.idle.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
}
